import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  Stack,
  Divider,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SpeedIcon from '@mui/icons-material/Speed';
import ForumIcon from '@mui/icons-material/Forum';
import FolderIcon from '@mui/icons-material/Folder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import usePageTitle from '../hooks/usePageTitle';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'et', label: 'Eesti keel' },
  { value: 'ru', label: 'Русский' },
  { value: 'fi', label: 'Suomi' },
  { value: 'lv', label: 'Latviešu' },
  { value: 'lt', label: 'Lietuvių' },
  { value: 'uk', label: 'Українська' },
];

const FEATURES = [
  { key: 'apartments', icon: ApartmentIcon },
  { key: 'billing', icon: ReceiptLongIcon },
  { key: 'meters', icon: SpeedIcon },
  { key: 'forum', icon: ForumIcon },
  { key: 'files', icon: FolderIcon },
  { key: 'ai', icon: AutoAwesomeIcon },
];

const STEPS = ['union', 'apartments', 'utilities', 'bills'];

function Landing() {
  const { t, i18n } = useTranslation();
  usePageTitle(null);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: '1px solid #E2E8F0' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: '-0.02em' }}>
            NAIT<Box component="span" sx={{ color: 'primary.main' }}>.EE</Box>
          </Typography>
          <Select
            size="small"
            value={i18n.language?.split('-')[0] || 'et'}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            sx={{ mr: 2, minWidth: 130 }}
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
            ))}
          </Select>
          <Button component={RouterLink} to="/login" variant="outlined" sx={{ mr: 1 }}>
            {t('auth.signIn')}
          </Button>
          <Button component={RouterLink} to="/register" variant="contained">
            {t('auth.signUp')}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 2 }}>
            {t('landing.heroTitle')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4 }}>
            {t('landing.heroSubtitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/register" variant="contained" size="large">
              {t('landing.ctaPrimary')}
            </Button>
            <Button component={RouterLink} to="/login" variant="outlined" size="large">
              {t('landing.ctaSecondary')}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 10 } }}>
        <Grid container spacing={3}>
          {FEATURES.map(({ key, icon: Icon }) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Icon sx={{ color: 'primary.main', fontSize: 32, mb: 1.5 }} />
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {t(`landing.features.${key}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`landing.features.${key}.desc`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h5" sx={{ mb: 4, textAlign: 'center' }}>
            {t('landing.howTitle')}
          </Typography>
          <Grid container spacing={3}>
            {STEPS.map((key, idx) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    mb: 1.5,
                  }}
                >
                  {idx + 1}
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t(`landing.steps.${key}.title`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`landing.steps.${key}.desc`)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Closing CTA */}
      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('landing.closingTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('landing.closingSubtitle')}
        </Typography>
        <Button component={RouterLink} to="/register" variant="contained" size="large">
          {t('landing.ctaPrimary')}
        </Button>
      </Container>

      <Divider />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          © {new Date().getFullYear()} NAIT.EE · {t('landing.madeBy')}{' '}
          <Box
            component="a"
            href="https://vire.ee"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Vire Lahendused OÜ
          </Box>
        </Typography>
      </Container>
    </Box>
  );
}

export default Landing;
